import { useCallback, useId, useMemo, useState } from 'react'

import type { ChangeEvent } from '../utils'
import type { RadioGroupProps } from './radioGroup.types'

import Box from '../box'
import { styled, vui } from '../core'
import {
  selectionControlGroupGap,
  selectionControlLabelSize,
  selectionControlStackGap,
} from '../selectionControl/theme'
import T from '../t'
import { cs, filterUndefined } from '../utils'
import { radioColors } from './consts'
import { RadioGroupProvider } from './context'

export const RadioGroupBase = styled.divBox`
  display: flex;
  flex-direction: column;
`

/**
 * Organizes layout and display of multiple radio buttons as a row or column.
 * Exposes some props to the children via context. `label`/`helpText`/`errorText` render an optional
 * group heading/description around the stack; `errorText` overrides `helpText` and marks the group invalid.
 */
export const RadioGroup = vui<'div', RadioGroupProps>((props, ref) => {
  const {
    children,
    className,
    defaultValue,
    disabled,
    errorText,
    gap,
    groupGap,
    helpText,
    isRow,
    label,
    name,
    onBlur,
    onChange,
    onFocus,
    size,
    value,
    variant,
    ...rest
  } = props
  const description = errorText ?? helpText
  const [isChecked, setIsChecked] = useState(value ?? defaultValue)

  const handleOnChange = useCallback(
    (e: ChangeEvent) => {
      setIsChecked(e.target.value)
      onChange?.(e)
    },
    [onChange],
  )

  const context = useMemo(
    () =>
      filterUndefined({
        defaultValue,
        disabled,
        isChecked,
        name,
        onBlur,
        onChange: handleOnChange,
        onFocus,
        size,
        value,
        variant,
      }),
    [defaultValue, disabled, handleOnChange, isChecked, name, onBlur, onFocus, size, value, variant],
  )

  const groupSize = size ?? 'md'
  // `gap` (SystemProps) always controls radio-to-radio spacing; `groupGap` only applies to the
  // label/help-text wrapper below, so the two can't collide when both are set.
  const aliasedProps = filterUndefined({
    flexDirection: isRow ? 'row' : undefined,
    gap: gap ?? selectionControlStackGap[groupSize] ?? selectionControlStackGap.md,
  } as const)

  const generatedId = useId()
  const labelId = label ? `${generatedId}-label` : undefined
  const descriptionId = description ? `${generatedId}-help-text` : undefined

  if (!label && !description) {
    return (
      <RadioGroupProvider value={context}>
        <RadioGroupBase className={cs('vui-radioGroup', className)} ref={ref} {...aliasedProps} {...rest}>
          {children}
        </RadioGroupBase>
      </RadioGroupProvider>
    )
  }

  return (
    <RadioGroupProvider value={context}>
      <Box
        className={cs('vui-radioGroup', className)}
        column
        gap={groupGap ?? selectionControlGroupGap[groupSize] ?? selectionControlGroupGap.md}
        ref={ref}
        {...rest}
      >
        {label && (
          <T
            className="vui-radioGroupLabel"
            color={radioColors.label}
            fontWeight="demi"
            id={labelId}
            size={selectionControlLabelSize[groupSize] ?? selectionControlLabelSize.md}
          >
            {label}
          </T>
        )}
        <RadioGroupBase
          aria-describedby={descriptionId}
          aria-invalid={!!errorText || undefined}
          aria-labelledby={labelId}
          className="vui-radioGroupStack"
          role="group"
          {...aliasedProps}
        >
          {children}
        </RadioGroupBase>
        {description && (
          <T
            className="vui-radioGroupHelpText"
            color={errorText ? radioColors.error : radioColors.helper}
            id={descriptionId}
            size="xs"
          >
            {description}
          </T>
        )}
      </Box>
    </RadioGroupProvider>
  )
})

RadioGroup.displayName = 'RadioGroup'

export default RadioGroup
